import React, { useEffect, useState } from "react";
import { Dialog } from 'primereact/dialog';
import { amenityService } from "../../service/amenity.service";

interface Amenity {

  id: number;

  name: string | null;

  description: string | null;

  iconName: string | null;

  parentAmenityId: number | null;

}

interface AmenityMapping {

  amenityId: number;

}

interface PropertyAmenitiesProps {
  propertyId: number;
  viewMode?: 'minimal' | 'full'; // Add view mode prop
}

const PropertyAmenities: React.FC<PropertyAmenitiesProps> = ({ propertyId, viewMode = 'full' }) => {
  const [amenityMaster, setAmenityMaster] = useState<Amenity[]>([]);
  const [mappedAmenityIds, setMappedAmenityIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showAllModal, setShowAllModal] = useState<boolean>(false);

  useEffect(() => {

    if (!propertyId) return;

    setLoading(true);

    Promise.all([

      amenityService.getAll(), // Get full amenity list (parents + children)

      amenityService.getAmenitiesByPropertyId(propertyId), // Get mapped amenities

    ])

      .then(([masterRes, mappedRes]) => {

        const masterList = Array.isArray(masterRes)
          ? masterRes
          : (masterRes && Array.isArray((masterRes as any).$values) ? (masterRes as any).$values : []);

        const mappedList: AmenityMapping[] = Array.isArray(mappedRes)
          ? mappedRes
          : (mappedRes && typeof mappedRes === "object" && "$values" in mappedRes && Array.isArray((mappedRes as any).$values)
            ? (mappedRes as any).$values
            : []);

        const mappedIds = mappedList.map((a) => a.amenityId);

        setAmenityMaster(masterList);

        setMappedAmenityIds(mappedIds);

      })

      .catch((err) => {

        console.error("Error loading amenities:", err);

        setAmenityMaster([]);

        setMappedAmenityIds([]);

      })

      .finally(() => setLoading(false));

  }, [propertyId]);

  const groupedAmenities = (() => {
    // Get only the mapped child amenities
    const mappedChildren = amenityMaster.filter(
      (a) => mappedAmenityIds.includes(a.id) && a.parentAmenityId !== null
    );

    // Find all unique parent IDs for those children
    const parentIds = Array.from(new Set(mappedChildren.map((a) => a.parentAmenityId!)));

    // Get parent objects from the master list
    const parents = amenityMaster.filter((a) => parentIds.includes(a.id));

    return parents.map((parent) => ({
      parent,
      children: mappedChildren.filter((child) => child.parentAmenityId === parent.id),
    }));
  })();

  // Get flat list of amenities for minimal view
  const getTopAmenities = () => {
    const allAmenities: { name: string; category: string }[] = [];
    
    groupedAmenities.forEach(group => {
      group.children.forEach(child => {
        if (child.name) {
          allAmenities.push({
            name: child.name,
            category: group.parent.name || 'Other'
          });
        }
      });
    });

    // Show first 6 amenities for minimal view
    return allAmenities.slice(0, 8);
  };

  const topAmenities = getTopAmenities();
  const totalAmenities = groupedAmenities.reduce((total, group) => total + group.children.length, 0);

  if (loading) {
    return <div className="text-center py-3">Loading amenities...</div>;
  }

  if (groupedAmenities.length === 0) {
    return <div className="text-muted fst-italic py-3">No amenities available.</div>;
  }

  // Minimal View
  if (viewMode === 'minimal') {
    return (
      <>
        {/* Minimal Amenities Display - 5 items per row */}
        <style>{`
          .amenities-grid {
            display: grid;
            gap: 1rem;
            grid-auto-rows: minmax(auto, 1fr);
          }
          
          @media (min-width: 1200px) {
            .amenities-grid {
              grid-template-columns: repeat(5, 1fr);
            }
          }
          
          @media (min-width: 768px) and (max-width: 1199px) {
            .amenities-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
          
          @media (max-width: 767px) {
            .amenities-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
        `}</style>
        <div className="amenities-grid">
          {topAmenities.map((amenity, index) => (
            <div key={index} className="amenity-item">
              <div className="d-flex align-items-center">
                <i className="bi bi-check-circle-fill text-success me-2" style={{ fontSize: '14px' }}></i>
                <span className="tx-14 text-dark">{amenity.name}</span>
              </div>
            </div>
          ))}
          
          {/* Show More Button as grid item */}
          {totalAmenities > 7 && (
            <div className="amenity-item">
              <div 
                className="d-flex align-items-center" 
                style={{ cursor: 'pointer' }}
                onClick={() => setShowAllModal(true)}
              >
                <i className="bi bi-plus-circle text-primary me-2" style={{ fontSize: '14px' }}></i>
                <span className="tx-14 text-primary fw-semibold">More Amenities</span>
              </div>
            </div>
          )}
        </div>

        {/* Full Amenities Modal */}
        <Dialog
          header="All Property Amenities"
          visible={showAllModal}
          style={{ width: '85vw', maxWidth: '1000px' }}
          onHide={() => setShowAllModal(false)}
          draggable={false}
          resizable={false}
          modal
          className="amenities-modal"
        >
          <PropertyAmenities propertyId={propertyId} viewMode="full" />
        </Dialog>
      </>
    );
  }

  // Full View (default)
  return (
    <>
      <div className="bg-white rounded-4 shadow-sm">
        <div className="row">
          {groupedAmenities.map((group, index) => (
            <div className="col-md-3" key={index}>
              <h6 className="fw-semibold mb-2 px-2 py-2">{group.parent.name}</h6>
              <div>
                {group.children.length > 0 ? (
                  group.children.map((child) => (
                    <div key={child.id} className="mb-2 d-flex px-2 align-items-start">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      <div>
                        <p className="tx-14 fw-normal mb-0">{child.name ?? "-"}</p>
                        {child.iconName &&
                          child.iconName.toLowerCase() !== "na" && (
                            <div className="text-muted">
                              {/* Icon: {child.iconName} */}
                            </div>
                          )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted fst-italic">No child amenities.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

};

export default PropertyAmenities;

